import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio/radio.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '../components/order-qr';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import {
  IIngredientSnapshot,
  IProduct,
  ISale,
  ISaleLine,
  IStockItem,
  RefundMethod,
  SaleStatus,
} from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import { getOrCreateDeviceId } from '../datatypes/device';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Full-page detail for an active order — the worker / kitchen view.
// Replaces the previous drawer pattern. Deliberately *does not* show
// prices, costs, or totals: by the time an order reaches this page it has
// already been paid, and the people fulfilling it (barista, kitchen) don't
// need that information to make the drink. Only the things you'd shout
// across the bar are surfaced: item + qty, modifier selections, and the
// ingredients to pull off the shelf.
//
// Future hook: per-recipe instructions display — IProduct doesn't carry
// an instructions field yet, so the recipe block is left ready for it.
@customElement('active-order-detail-page')
export class ActiveOrderDetailPage extends Base {
  @state() private _saleId: string | null = null;
  @state() private _sale: ISale | null = null;
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _busy = false;

  @state() private _menuById: Map<string, IProduct> = new Map();
  @state() private _stockById: Map<string, IStockItem> = new Map();

  // Cancel-order flow.
  @state() private _cancelOpen = false;
  @state() private _cancelRefund = true;
  @state() private _cancelMethod: RefundMethod = 'cash';
  @state() private _cancelReason = '';
  @state() private _cancelSkipConfirm = false;
  @state() private _cancelBusy = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        /* Cap at a comfortable reading width on large viewports — tables
           and the meta grid sprawl past ~960 otherwise. Host owns the
           gutter padding directly so phones don't get content flush
           against the sidebar / drawer edge. */
        width: 100%;
        max-width: 960px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      /* Shoelace's <sl-card> defaults to display: inline-block, so it
         shrinks to the width of its widest line. Without the totals
         block / line-head price, the widest content is just an ingredient
         row, which made the card visibly narrower than the host. Force
         it to fill the host. */
      sl-card {
        display: block;
        width: 100%;
      }
      .header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
      }
      .header h2 {
        margin: 0;
        flex: 1;
      }
      /* Phase Y — order number leads the title, monospace +
         tabular nums so it reads as a number not a code. */
      .header h2 .title-order-number {
        font-family: var(
          --font-mono,
          ui-monospace,
          SFMono-Regular,
          Menlo,
          monospace
        );
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.01em;
      }
      .meta-grid {
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 4px 12px;
        margin-bottom: 12px;
        font-size: 0.9rem;
      }
      .meta-grid dt {
        opacity: 0.7;
      }
      .meta-grid dd {
        margin: 0;
        font-weight: 600;
      }
      .line {
        padding: 12px 0;
        border-bottom: 1px solid var(--sl-color-neutral-100);
      }
      .line:last-child {
        border-bottom: none;
      }
      .line-head {
        display: flex;
        align-items: baseline;
        gap: 12px;
      }
      .line-head .name {
        font-weight: 600;
        font-size: 1.05rem;
      }
      .options {
        margin: 4px 0 0;
        opacity: 0.8;
        font-size: 0.85rem;
      }
      .recipe {
        margin-top: 10px;
        padding: 8px 10px;
        background: var(--sl-color-neutral-50);
        border-radius: var(--sl-border-radius-medium);
      }
      .recipe h4 {
        margin: 0 0 4px;
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--sl-color-neutral-600);
      }
      .recipe table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.85rem;
      }
      .recipe td {
        padding: 2px 0;
      }
      .recipe td.qty {
        text-align: right;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
        padding-left: 12px;
      }
      .footer-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        margin-top: 16px;
      }
      .footer-actions .cancel-cta::part(base) {
        color: var(--sl-color-danger-600);
      }
      .center {
        display: flex;
        justify-content: center;
        padding: 32px;
      }
    `,
  ];

  firstUpdated() {
    this._saleId = readSaleIdFromPath();
    if (!this._saleId) {
      this._error = 'Missing order id.';
      this._loading = false;
      return;
    }
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    void this._load();
  }

  updated(changed: Map<string, unknown>) {
    if (
      changed.has('activeShop') &&
      this.activeShop &&
      this._saleId &&
      !this._sale
    ) {
      void this._load();
    }
  }

  private async _load() {
    if (!this.activeShop || !this._saleId) return;
    this._loading = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    const [sale, menu, stock] = await Promise.all([
      api.getSale(this.activeShop.shopName, this._saleId),
      api.listMenu(this.activeShop.shopName),
      api.listStock(this.activeShop.shopName),
    ]);
    if (!sale.success || !sale.data) {
      this._error = sale.error ?? 'Order not found.';
      this._loading = false;
      return;
    }
    this._sale = sale.data;
    if (menu.success && menu.data) {
      const m = new Map<string, IProduct>();
      for (const item of menu.data) m.set(item.id, item);
      this._menuById = m;
    }
    if (stock.success && stock.data) {
      const s = new Map<string, IStockItem>();
      for (const item of stock.data) s.set(item.id, item);
      this._stockById = s;
    }
    this._loading = false;
  }

  private _back() {
    void router.navigate(resolveRouterPath('orders/active'));
  }

  private _nextStatus(s: SaleStatus): SaleStatus | null {
    return s === 'paid'
      ? 'preparing'
      : s === 'preparing'
        ? 'ready'
        : s === 'ready'
          ? 'completed'
          : null;
  }

  private _ctaLabel(s: SaleStatus): string {
    return s === 'paid'
      ? 'Start preparing'
      : s === 'preparing'
        ? 'Mark ready'
        : 'Complete';
  }

  private _statusName(s: SaleStatus): string {
    switch (s) {
      case 'paid':
        return 'Paid';
      case 'preparing':
        return 'Preparing';
      case 'ready':
        return 'Ready';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'pending_payment':
        return 'Pending payment';
    }
  }

  private async _changeStatus(target: SaleStatus) {
    if (!this.activeShop || !this._sale || this._busy) return;
    this._busy = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    const r = await api.transitionSaleStatus(
      this.activeShop.shopName,
      this._sale.id,
      target
    );
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Status change failed.';
      return;
    }
    if (target === 'completed') {
      // Terminal — pop back to the board.
      this._back();
      return;
    }
    this._sale = r.data;
  }

  private _openCancel() {
    this._cancelOpen = true;
    this._cancelRefund = true;
    this._cancelMethod = (
      this._sale?.tenders?.some((t) => t.method === 'card') ? 'card' : 'cash'
    ) as RefundMethod;
    this._cancelReason = '';
    this._cancelSkipConfirm = false;
  }

  private async _submitCancel() {
    if (this._cancelBusy || !this.activeShop || !this._sale) return;
    if (!this._cancelRefund && !this._cancelSkipConfirm) {
      this._cancelSkipConfirm = true;
      return;
    }
    this._cancelBusy = true;
    this._error = null;
    try {
      const api = ApiFactory.authenticated();
      const deviceId = await getOrCreateDeviceId();
      const r = await api.cancelSale(this.activeShop.shopName, this._sale.id, {
        refundPayment: this._cancelRefund,
        refundMethod: this._cancelMethod,
        deviceId,
        reason: this._cancelReason.trim() || undefined,
      });
      if (!r.success) {
        this._error = r.error ?? 'Could not cancel the order.';
        return;
      }
      // Terminal — pop back to the board.
      this._back();
    } finally {
      this._cancelBusy = false;
    }
  }

  private _formatTime(utc?: string | null): string {
    if (!utc) return '—';
    return new Date(utc).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private _resolveOptionsText(line: ISaleLine): string {
    if (
      !line.selectedModifierGroups ||
      line.selectedModifierGroups.length === 0
    )
      return '';
    const item = this._menuById.get(line.productId);
    const parts: string[] = [];
    for (const sel of line.selectedModifierGroups) {
      if (sel.selections.length === 0) continue;
      const group = item?.modifierGroups.find((g) => g.id === sel.groupId);
      const names = sel.selections.map((entry) => {
        const opt = group?.modifiers.find((o) => o.id === entry.modifierId);
        const name = opt?.name ?? entry.modifierId;
        // Show "×N" only when the option is quantifiable and qty > 1.
        // Non-quantifiable picks always read at qty 1 implicitly.
        return opt?.quantifiable && entry.quantity > 1
          ? `${name} ×${entry.quantity}`
          : name;
      });
      const groupName = group?.name ?? sel.groupId;
      parts.push(`${groupName}: ${names.join(', ')}`);
    }
    return parts.join(' · ');
  }

  private _stockName(stockItemId: string): string {
    return this._stockById.get(stockItemId)?.name ?? stockItemId;
  }

  private _stockUnit(stockItemId: string): string {
    return this._stockById.get(stockItemId)?.baseUnit ?? '';
  }

  private _renderRecipe(recipe: IIngredientSnapshot[] | null | undefined) {
    if (!recipe || recipe.length === 0) return nothing;
    return html`
      <div class="recipe">
        <h4>Ingredients to pull</h4>
        <table>
          <tbody>
            ${recipe.map(
              (ing) => html`
                <tr>
                  <td>${this._stockName(ing.stockItemId)}</td>
                  <td class="qty">
                    ${ing.quantity} ${this._stockUnit(ing.stockItemId)}
                  </td>
                </tr>
              `
            )}
          </tbody>
        </table>
      </div>
    `;
  }

  private _renderLine(line: ISaleLine, status: SaleStatus) {
    const optionsText = this._resolveOptionsText(line);
    // Ingredient breakdown only matters while the order is being made.
    // Paid orders haven't started; Ready orders are waiting handover. In
    // both cases the worker just needs items + modifiers, not the recipe.
    const showRecipe = status === 'preparing';
    return html`
      <div class="line">
        <div class="line-head">
          <span class="name">${line.quantity} × ${line.nameSnapshot}</span>
        </div>
        ${optionsText ? html`<div class="options">${optionsText}</div>` : nothing}
        ${showRecipe ? this._renderRecipe(line.recipe) : nothing}
      </div>
    `;
  }

  render() {
    if (this._loading) {
      return html`<div class="center"><sl-spinner></sl-spinner></div>`;
    }
    if (this._error || !this._sale) {
      return html`
        <div class="header">
          <sl-button variant="default" @click=${() => this._back()}>
            <sl-icon slot="prefix" name="arrow-left"></sl-icon>
            Back
          </sl-button>
        </div>
        <sl-alert variant="danger" open
          >${this._error ?? 'Order not found.'}</sl-alert
        >
      `;
    }

    const sale = this._sale;
    const next = this._nextStatus(sale.status);
    const labelSource = sale.pickupCode
      ? `#${sale.pickupCode}`
      : sale.tableTag
        ? `Table ${sale.tableTag}`
        : sale.guestContactName
          ? sale.guestContactName
          : this._formatTime(sale.paidUtc);
    // Phase Y — lead the title with the cashier-visible day sequence
    // ("Order #42 · 17:41") whenever the sale has one. Legacy sales
    // from before Y.A simply don't show a number — same conservative
    // rule as the active-orders board (we never want a fallback that
    // looks like a code).
    const hasNumber = sale.daySequence && sale.daySequence > 0;

    return html`
      <div class="header">
        <sl-button variant="default" @click=${() => this._back()}>
          <sl-icon slot="prefix" name="arrow-left"></sl-icon>
          Back
        </sl-button>
        <h2>
          Order
          ${
            hasNumber
              ? html` <span class="title-order-number"
                  >#${sale.daySequence}</span
                >`
              : nothing
          }
          · ${labelSource}
        </h2>
        <sl-badge
          variant=${sale.status === 'ready' ? 'success' : sale.status === 'preparing' ? 'warning' : 'neutral'}
          pill
        >
          ${this._statusName(sale.status)}
        </sl-badge>
      </div>

      <sl-card>
        <dl class="meta-grid">
          <dt>Source</dt>
          <dd>
            ${sale.source === 'guest_qr' ? 'Guest QR' : sale.source === 'online' ? 'Online' : 'Counter'}
          </dd>
          ${
            sale.pickupCode
              ? html`<dt>Pickup code</dt>
                  <dd>${sale.pickupCode}</dd>`
              : nothing
          }
          ${
            sale.tableTag
              ? html`<dt>Table</dt>
                  <dd>${sale.tableTag}</dd>`
              : nothing
          }
          ${
            sale.guestContactName
              ? html`<dt>Guest</dt>
                  <dd>${sale.guestContactName}</dd>`
              : nothing
          }
          <dt>Paid at</dt>
          <dd>${this._formatTime(sale.paidUtc)}</dd>
          ${
            sale.preparedUtc
              ? html`<dt>Started prep</dt>
                  <dd>${this._formatTime(sale.preparedUtc)}</dd>`
              : nothing
          }
        </dl>

        ${
          sale.trackToken
            ? html`
                <sl-divider></sl-divider>
                <div
                  style="display: flex; justify-content: center; padding: 12px 0;"
                >
                  <order-qr
                    .token=${sale.trackToken}
                    .daySequence=${sale.daySequence ?? 0}
                    size=${220}
                  ></order-qr>
                </div>
              `
            : nothing
        }

        <sl-divider></sl-divider>

        ${sale.lines.map((line) => this._renderLine(line, sale.status))}
        ${this._error ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>` : nothing}

        <div class="footer-actions">
          <sl-button
            class="cancel-cta"
            variant="default"
            ?disabled=${this._busy}
            @click=${() => this._openCancel()}
            >Cancel order</sl-button
          >
          ${
            next
              ? html`
                  <sl-button
                    variant="primary"
                    ?loading=${this._busy}
                    ?disabled=${this._busy}
                    @click=${() => this._changeStatus(next)}
                    >${this._ctaLabel(sale.status)}</sl-button
                  >
                `
              : nothing
          }
        </div>
      </sl-card>

      ${this._renderCancelDialog(sale)}
    `;
  }

  private _renderCancelDialog(sale: ISale) {
    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Cancel order"
        ?open=${this._cancelOpen}
        @sl-request-close=${() => (this._cancelOpen = false)}
      >
        <p>
          Cancel order
          ${sale.daySequence > 0 ? html`<strong>#${sale.daySequence}</strong>` : nothing}
          (total <strong>${sale.total.toFixed(2)}</strong>)? This takes it off
          the board and cannot be undone.
        </p>

        <sl-switch
          ?checked=${this._cancelRefund}
          @sl-change=${(e: Event) => {
            this._cancelRefund = (e.target as HTMLInputElement).checked;
            this._cancelSkipConfirm = false;
          }}
          >Refund the customer ${sale.total.toFixed(2)}</sl-switch
        >

        ${
          this._cancelRefund
            ? html`<sl-radio-group
                label="Refund method"
                .value=${this._cancelMethod}
                @sl-change=${(e: Event) =>
                  (this._cancelMethod = (e.target as HTMLInputElement)
                    .value as RefundMethod)}
              >
                <sl-radio value="cash"
                  >Cash (returns money from the till)</sl-radio
                >
                <sl-radio value="card"
                  >Card (process on the card terminal)</sl-radio
                >
                <sl-radio value="other">Other / store credit</sl-radio>
              </sl-radio-group>`
            : html`<sl-alert variant="warning" open>
                No refund will be issued — the customer's
                ${sale.total.toFixed(2)} is not returned by the system.
              </sl-alert>`
        }

        <sl-textarea
          label="Reason (optional)"
          rows="2"
          .value=${this._cancelReason}
          @sl-input=${(e: Event) =>
            (this._cancelReason = (e.target as HTMLTextAreaElement).value)}
        ></sl-textarea>

        ${
          this._cancelSkipConfirm
            ? html`<sl-alert variant="danger" open
                >Cancel <strong>without</strong> refunding
                ${sale.total.toFixed(2)}? Tap the button again to
                confirm.</sl-alert
              >`
            : nothing
        }

        <sl-button slot="footer" @click=${() => (this._cancelOpen = false)}
          >Keep order</sl-button
        >
        <sl-button
          slot="footer"
          variant="danger"
          ?loading=${this._cancelBusy}
          ?disabled=${this._cancelBusy}
          @click=${() => this._submitCancel()}
          >${
            this._cancelRefund
              ? 'Cancel + refund'
              : this._cancelSkipConfirm
                ? 'Confirm cancel, no refund'
                : 'Cancel order'
          }</sl-button
        >
      </sl-dialog>
    `;
  }
}

// Routed as /orders/active/:id — pull the id back out of the URL.
function readSaleIdFromPath(): string | null {
  const m = window.location.pathname.match(/\/orders\/active\/([^/?#]+)/);
  if (!m) return null;
  return decodeURIComponent(m[1]);
}
